// §34.3 shop bays + availability-first job scheduling (INV-34-AVAIL-TRUTH).
//
// Pure functions operating on plain data - no Supabase types, no Date-object
// state beyond what's needed for interval math - so the client and server
// share exactly one implementation of "is this bay free / what does this
// day look like." Every instant here is an ISO timestamp string (already
// resolved to a real UTC instant by the caller); this module never touches
// timezone conversion itself (that's server/src/lib/timezone.ts +
// packages/shared/src/booking.ts, same split as §34.2's booking-window
// logic). A day's operating window is passed in as `dayWindow` - either the
// pair of ISO instants the shop is open that day, already computed by the
// caller from workspaces.operating_hours + workspaces.timezone, or `null`
// when the shop is closed that day - so this module never has to reason
// about minutes-from-midnight/timezone offsets at all.
const THIRTY_MINUTES_MS = 30 * 60_000;
const MS_PER_MINUTE = 60_000;
function toMs(iso) {
    return new Date(iso).getTime();
}
/** Half-open-interval overlap ([start, end)), matching Postgres tstzrange's
 * default bounds - the same semantics the DB's gist exclusion constraint
 * (INV-34-BAY-EXCL) enforces as the final backstop. */
function overlaps(aStart, aEnd, bStart, bEnd) {
    return aStart < bEnd && bStart < aEnd;
}
/** Clip [start, end) to a window, or null if there's no overlap at all. */
function clip(start, end, windowStart, windowEnd) {
    const s = Math.max(start, windowStart);
    const e = Math.min(end, windowEnd);
    return e > s ? [s, e] : null;
}
/** Merge (already-clipped) intervals and return the gaps between them
 * within [windowStart, windowEnd) - i.e. this bay's free time that day. */
function freeIntervals(busy, windowStart, windowEnd) {
    const sorted = [...busy].sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const [s, e] of sorted) {
        const last = merged[merged.length - 1];
        if (last && s <= last[1]) {
            last[1] = Math.max(last[1], e);
        }
        else {
            merged.push([s, e]);
        }
    }
    const free = [];
    let cursor = windowStart;
    for (const [s, e] of merged) {
        if (s > cursor)
            free.push([cursor, s]);
        cursor = Math.max(cursor, e);
    }
    if (cursor < windowEnd)
        free.push([cursor, windowEnd]);
    return free;
}
/** §34.3 INV-34-AVAIL-TRUTH - the single day-level rollup used by both the
 * range availability endpoint and (indirectly) the client's month view.
 * tight = >=80% of bay-minutes OR installer-minutes booked; full = no
 * 30-minute slot placeable in any active bay, or installer minutes
 * exhausted; closed = dayWindow null. */
export function computeDayAvailability(args) {
    const { date, dayWindow, bays, segments, installerCount } = args;
    const activeBays = bays.filter((b) => b.isActive);
    const bayCount = activeBays.length;
    if (!dayWindow) {
        return { date, state: 'closed', baysFreeAtPeak: 0, bayCount, installerMinutesFree: 0, installerMinutesTotal: 0 };
    }
    const windowStart = toMs(dayWindow.startsAt);
    const windowEnd = toMs(dayWindow.endsAt);
    const windowMinutes = (windowEnd - windowStart) / MS_PER_MINUTE;
    const activeBayIds = new Set(activeBays.map((b) => b.bayId));
    const clippedByBay = new Map();
    for (const seg of segments) {
        if (!activeBayIds.has(seg.bayId))
            continue;
        const c = clip(toMs(seg.startsAt), toMs(seg.endsAt), windowStart, windowEnd);
        if (!c)
            continue;
        const list = clippedByBay.get(seg.bayId) ?? [];
        list.push(c);
        clippedByBay.set(seg.bayId, list);
    }
    let bookedBayMinutes = 0;
    let anyThirtyMinSlot = false;
    for (const bay of activeBays) {
        const busy = clippedByBay.get(bay.bayId) ?? [];
        for (const [s, e] of busy)
            bookedBayMinutes += (e - s) / MS_PER_MINUTE;
        if (!anyThirtyMinSlot) {
            const free = freeIntervals(busy, windowStart, windowEnd);
            if (free.some(([s, e]) => e - s >= THIRTY_MINUTES_MS))
                anyThirtyMinSlot = true;
        }
    }
    const edges = [];
    for (const [bayId, busy] of clippedByBay) {
        if (!activeBayIds.has(bayId))
            continue;
        for (const [s, e] of busy) {
            edges.push({ t: s, delta: 1 });
            edges.push({ t: e, delta: -1 });
        }
    }
    edges.sort((a, b) => a.t - b.t || a.delta - b.delta);
    let concurrent = 0;
    let peakConcurrent = 0;
    for (const edge of edges) {
        concurrent += edge.delta;
        if (concurrent > peakConcurrent)
            peakConcurrent = concurrent;
    }
    const baysFreeAtPeak = Math.max(0, bayCount - peakConcurrent);
    const installerMinutesTotal = installerCount * windowMinutes;
    let bookedInstallerMinutes = 0;
    for (const seg of segments) {
        if (seg.kind !== 'work')
            continue; // cure hold occupies the bay, not an installer
        const c = clip(toMs(seg.startsAt), toMs(seg.endsAt), windowStart, windowEnd);
        if (c)
            bookedInstallerMinutes += (c[1] - c[0]) / MS_PER_MINUTE;
    }
    const installerMinutesFree = Math.max(0, installerMinutesTotal - bookedInstallerMinutes);
    const totalBayMinutes = bayCount * windowMinutes;
    const bayUtilization = totalBayMinutes > 0 ? bookedBayMinutes / totalBayMinutes : 1;
    const installerUtilization = installerMinutesTotal > 0 ? bookedInstallerMinutes / installerMinutesTotal : 1;
    let state;
    if (bayCount === 0 || (!anyThirtyMinSlot) || installerMinutesFree <= 0) {
        state = 'full';
    }
    else if (bayUtilization >= 0.8 || installerUtilization >= 0.8) {
        state = 'tight';
    }
    else {
        state = 'available';
    }
    return { date, state, baysFreeAtPeak, bayCount, installerMinutesFree, installerMinutesTotal };
}
/** §34.3 INV-34-AVAIL-TRUTH / INV-34-CURE-BAY - validates a single proposed
 * placement (a job's work segment) against the bays that exist and the
 * segments already scheduled that day. `cure_hold` segments block a bay
 * exactly like `work` segments do (INV-34-CURE-BAY: a 24h cure hold in a bay
 * blocks that whole bay for the window, unaffected by the placement's own
 * `kind`). */
export function validatePlacement(placement, segments, dayWindow, bays) {
    const bay = bays.find((b) => b.bayId === placement.bayId);
    if (!bay)
        return { ok: false, reason: 'bay_not_found' };
    if (!bay.isActive)
        return { ok: false, reason: 'bay_inactive' };
    if (!dayWindow)
        return { ok: false, reason: 'outside_hours' };
    const windowStart = toMs(dayWindow.startsAt);
    const windowEnd = toMs(dayWindow.endsAt);
    const start = toMs(placement.startsAt);
    const end = toMs(placement.endsAt);
    if (start < windowStart || end > windowEnd)
        return { ok: false, reason: 'outside_hours' };
    const conflict = segments.some((seg) => seg.bayId === placement.bayId && overlaps(start, end, toMs(seg.startsAt), toMs(seg.endsAt)));
    if (conflict)
        return { ok: false, reason: 'bay_conflict' };
    return { ok: true };
}
/** Slice `durationMinutes` of work across the ordered `days` list, starting
 * at `startsAt` on day 1. Closed days (window null) after day 1 are skipped
 * (no span). Day 1's start must lie within its own window, else outside_hours;
 * if the days run out before the work is placed, insufficient_days. A job that
 * fits in one day returns exactly one span; zero-minute spans are never
 * emitted. */
export function planDaySpans(args) {
    const { startsAt, durationMinutes, days } = args;
    const spans = [];
    let remaining = durationMinutes;
    let isFirst = true;
    for (const day of days) {
        if (remaining <= 0)
            break;
        if (isFirst) {
            isFirst = false;
            if (!day.window)
                return { ok: false, reason: 'outside_hours' };
            const startMs = toMs(startsAt);
            const windowStart = toMs(day.window.startsAt);
            const windowEnd = toMs(day.window.endsAt);
            if (startMs < windowStart || startMs >= windowEnd)
                return { ok: false, reason: 'outside_hours' };
            const available = (windowEnd - startMs) / MS_PER_MINUTE;
            const take = Math.min(remaining, available);
            spans.push({ date: day.date, startsAt: new Date(startMs).toISOString(), endsAt: new Date(startMs + take * MS_PER_MINUTE).toISOString(), minutes: take });
            remaining -= take;
            continue;
        }
        if (!day.window)
            continue;
        const windowStart = toMs(day.window.startsAt);
        const windowEnd = toMs(day.window.endsAt);
        const available = (windowEnd - windowStart) / MS_PER_MINUTE;
        if (available <= 0)
            continue;
        const take = Math.min(remaining, available);
        spans.push({ date: day.date, startsAt: new Date(windowStart).toISOString(), endsAt: new Date(windowStart + take * MS_PER_MINUTE).toISOString(), minutes: take });
        remaining -= take;
    }
    if (remaining > 0)
        return { ok: false, reason: 'insufficient_days' };
    return { ok: true, spans };
}
/** Validates every span of a multi-day placement against the bays that exist
 * and the segments already scheduled in the placement's range. Collects ALL
 * failing spans (bay missing/inactive or overlapping a segment in that bay),
 * not just the first, so the caller can name every blocked day. Span/hours
 * validity is assumed already checked by planDaySpans. */
export function validateMultiDayPlacement(placement, segments, bays) {
    const failures = [];
    for (const span of placement) {
        const bay = bays.find((b) => b.bayId === span.bayId);
        if (!bay) {
            failures.push({ date: span.date, bayId: span.bayId, reason: 'bay_not_found' });
            continue;
        }
        if (!bay.isActive) {
            failures.push({ date: span.date, bayId: span.bayId, reason: 'bay_inactive' });
            continue;
        }
        const start = toMs(span.startsAt);
        const end = toMs(span.endsAt);
        const conflict = segments.some((seg) => seg.bayId === span.bayId && overlaps(start, end, toMs(seg.startsAt), toMs(seg.endsAt)));
        if (conflict)
            failures.push({ date: span.date, bayId: span.bayId, reason: 'bay_conflict' });
    }
    if (failures.length > 0)
        return { ok: false, failures };
    return { ok: true };
}
/** Active bays with no segment overlapping this span's interval. */
export function availableBaysForSpan(span, segments, bays) {
    const start = toMs(span.startsAt);
    const end = toMs(span.endsAt);
    return bays
        .filter((b) => b.isActive)
        .filter((bay) => !segments.some((seg) => seg.bayId === bay.bayId && overlaps(start, end, toMs(seg.startsAt), toMs(seg.endsAt))))
        .map((b) => b.bayId);
}
/** Per-bay list of valid 30-minute-aligned start times (ISO instants) for a
 * segment of `durationMinutes`, within `dayWindow`, that would pass
 * validatePlacement for that bay. */
export function listOpenSlotsForDay(bays, segments, dayWindow, durationMinutes) {
    if (!dayWindow || durationMinutes <= 0)
        return bays.filter((b) => b.isActive).map((b) => ({ bayId: b.bayId, slots: [] }));
    const windowStart = toMs(dayWindow.startsAt);
    const windowEnd = toMs(dayWindow.endsAt);
    const durationMs = durationMinutes * MS_PER_MINUTE;
    return bays
        .filter((b) => b.isActive)
        .map((bay) => {
        const slots = [];
        for (let start = windowStart; start + durationMs <= windowEnd; start += THIRTY_MINUTES_MS) {
            const end = start + durationMs;
            const result = validatePlacement({ bayId: bay.bayId, startsAt: new Date(start).toISOString(), endsAt: new Date(end).toISOString(), kind: 'work' }, segments, dayWindow, bays);
            if (result.ok)
                slots.push(new Date(start).toISOString());
        }
        return { bayId: bay.bayId, slots };
    });
}
//# sourceMappingURL=scheduling.js.map